import React, { useState } from 'react'
import { Button } from '@/components/ui/button.jsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.jsx'
import { Input } from '@/components/ui/input.jsx'
import { Label } from '@/components/ui/label.jsx'
import { Textarea } from '@/components/ui/textarea.jsx'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.jsx'
import { X, CheckCircle, AlertCircle } from 'lucide-react'

const InvestorForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    investmentAmount: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    try {
      const response = await fetch('/.netlify/functions/investor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const result = await response.json()

      if (response.ok && result.success) {
        setSubmitStatus('success')
        setTimeout(() => {
          onClose()
          setSubmitStatus(null)
          setFormData({
            name: '',
            email: '',
            phone: '',
            investmentAmount: '',
            message: ''
          })
        }, 2000)
      } else {
        setSubmitStatus('error')
        console.error('Failed to send email:', result.error)
      }
    } catch (error) {
      setSubmitStatus('error')
      console.error('Error submitting form:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Investment Interest</CardTitle>
              <CardDescription>Tell us about your investment goals</CardDescription>
            </div>
            <button
              onClick={onClose}
              className="text-gray-900 hover:text-primary hover:bg-gray-100 p-2 rounded-lg transition-colors flex-shrink-0"
              aria-label="Close"
              type="button"
            >
              <X className="h-6 w-6" style={{ color: '#000000', strokeWidth: 2 }} />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {submitStatus === 'success' ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 mx-auto mb-4" style={{ color: '#962831' }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#962831' }}>Thank You!</h3>
              <p className="text-gray-600">We'll be in touch within 24 hours.</p>
            </div>
          ) : submitStatus === 'error' ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-600 mb-2">Failed to Submit</h3>
              <p className="text-gray-600 mb-4">There was an error submitting your interest. Please try again.</p>
              <Button onClick={() => setSubmitStatus(null)} variant="outline">
                Try Again
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="mb-2 block text-gray-900">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="email" className="mb-2 block text-gray-900">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone" className="mb-2 block text-gray-900">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="investment" className="mb-2 block text-gray-900">Investment Range</Label>
                <Select onValueChange={(value) => handleChange('investmentAmount', value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select investment range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25k-50k">$25K - $50K</SelectItem>
                    <SelectItem value="50k-100k">$50K - $100K</SelectItem>
                    <SelectItem value="100k-250k">$100K - $250K</SelectItem>
                    <SelectItem value="250k+">$250K+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="mb-2 block text-gray-900">Message</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Tell us about your investment interests..."
                  rows={3}
                />
              </div>
              
              <Button
                type="submit"
                className="w-full bg-primary text-white hover:bg-primary/90 hover:text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Interest'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default InvestorForm

